import { useEffect, useState, useRef } from 'react';
import { fetchWithCorpId, fetchWithCorpIdAndFormData } from '../utils/api';
import { useNavigate } from 'react-router-dom';

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}><path d="M12 5v14M5 12l7-7 7 7"/></svg>;
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'active' ? 'badge badge-success' : s === 'inactive' ? 'badge badge-danger' : 'badge badge-neutral';
  return <span className={cls}>{status}</span>;
};

function CorpTable({ onEdit, selectedCorp, onCorpSwitch }) {
  const [corps, setCorps] = useState([]);
  const [filteredCorps, setFilteredCorps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ name: '', address: '', city: '', state: '', zip: '', status: '' });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [failedLogos, setFailedLogos] = useState(new Set());
  const [logoUrls, setLogoUrls] = useState({});
  const menuRef = useRef();
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchCorps();
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [page, size]);
  useEffect(() => { applyFilters(); }, [filter, corps]);
  useEffect(() => { applySorting(); }, [sortConfig]);

  const handleClickOutside = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {}
  };

  const fetchCorps = async () => {
    try {
      const data = await fetchWithCorpId(`/corps?page=${page}&size=${size}`);
      if (Array.isArray(data)) {
        setCorps(data); setFilteredCorps(data);
        setTotalElements(data.length); setTotalPages(1);
        loadLogos(data);
      } else {
        const { content = [], totalElements: total = 0, totalPages: totalP = 0 } = data || {};
        setCorps(content); setFilteredCorps(content);
        setTotalElements(total); setTotalPages(totalP || (content.length ? 1 : 0));
        loadLogos(content);
      }
    } catch { setError('Failed to fetch corporations'); } finally { setLoading(false); }
  };

  const loadLogos = async (list) => {
    const urls = {};
    for (const corp of list) {
      try {
        const res = await fetchWithCorpId(`/corps/${corp.id}/logo`, { headers: { 'Accept': 'image/*' } });
        urls[corp.id] = res.ok ? URL.createObjectURL(await res.blob()) : '/default-logo.png';
      } catch { urls[corp.id] = '/default-logo.png'; }
    }
    setLogoUrls(urls);
  };

  const applyFilters = () => {
    setFilteredCorps(corps.filter(c =>
      (!filter.name || c.name?.toLowerCase().includes(filter.name.toLowerCase())) &&
      (!filter.address || c.address?.toLowerCase().includes(filter.address.toLowerCase())) &&
      (!filter.city || c.city?.toLowerCase().includes(filter.city.toLowerCase())) &&
      (!filter.state || c.state?.toLowerCase().includes(filter.state.toLowerCase())) &&
      (!filter.zip || c.zip?.toLowerCase().includes(filter.zip.toLowerCase())) &&
      (!filter.status || c.status?.toLowerCase() === filter.status.toLowerCase())
    ));
  };

  const applySorting = () => {
    if (!sortConfig.key) return;
    setFilteredCorps(prev => [...prev].sort((a, b) => {
      const vA = a[sortConfig.key], vB = b[sortConfig.key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return sortConfig.direction === 'asc' ? vA - vB : vB - vA;
      return sortConfig.direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    }));
  };

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleLogoUpload = async (corpId, file) => {
    setUploadingLogo(corpId);
    try {
      const fd = new FormData(); fd.append('logo', file);
      await fetchWithCorpIdAndFormData(`/corps/${corpId}/logo`, fd, { method: 'PUT' });
      setFailedLogos(prev => { const s = new Set(prev); s.delete(corpId); return s; });
      await new Promise(r => setTimeout(r, 500));
      await fetchCorps();
    } catch { alert('Failed to upload logo. Please try again.'); } finally { setUploadingLogo(null); }
  };

  const handleLogoClick = (corpId) => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.onchange = (e) => { const f = e.target.files[0]; if (f) handleLogoUpload(corpId, f); };
      fileInputRef.current.click();
    }
  };

  const handleSwitch = (corp) => { localStorage.setItem('selectedCorpId', corp.id); onCorpSwitch(corp); };
  const handleChangePageSize = (e) => { setSize(parseInt(e.target.value, 10)); setPage(0); };

  return (
    <div className="page-container">
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Corporations</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Manage your registered corporations</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/on/corps/new')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Corporation
        </button>
      </div>

      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" />

      <div className="filter-bar">
        <input name="name" placeholder="Name" value={filter.name} onChange={handleFilterChange} />
        <input name="address" placeholder="Address" value={filter.address} onChange={handleFilterChange} />
        <input name="city" placeholder="City" value={filter.city} onChange={handleFilterChange} />
        <input name="state" placeholder="State" value={filter.state} onChange={handleFilterChange} />
        <input name="zip" placeholder="Zip" value={filter.zip} onChange={handleFilterChange} />
        <select name="status" value={filter.status} onChange={handleFilterChange}>
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button className="btn-secondary" onClick={() => setFilter({ name: '', address: '', city: '', state: '', zip: '', status: '' })}>Clear</button>
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading corporations…</div>
      ) : error ? (
        <div className="alert error">{error}</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Logo</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name <SortIcon col="name" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('address')}>Address <SortIcon col="address" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('city')}>City <SortIcon col="city" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('state')}>State <SortIcon col="state" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('zip')}>Zip <SortIcon col="zip" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status <SortIcon col="status" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('startDate')}>Start Date <SortIcon col="startDate" sortConfig={sortConfig} /></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCorps.length === 0 ? (
                <tr className="state-row"><td colSpan="9">No corporations found.</td></tr>
              ) : filteredCorps.map(corp => (
                <tr key={corp.id}>
                  <td>
                    <div
                      onClick={() => handleLogoClick(corp.id)}
                      title="Click to upload logo"
                      style={{ width: '40px', height: '40px', border: '1px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', background: '#f8fafc' }}
                    >
                      {uploadingLogo === corp.id ? (
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>...</span>
                      ) : failedLogos.has(corp.id) ? (
                        <span style={{ color: '#94a3b8', fontSize: '18px' }}>+</span>
                      ) : (
                        <img src={logoUrls[corp.id] || '/default-logo.png'} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={() => setFailedLogos(prev => new Set([...prev, corp.id]))} />
                      )}
                    </div>
                  </td>
                  <td><span className="table-link" onClick={() => onEdit(corp)}>{corp.name}</span></td>
                  <td>{corp.address}</td>
                  <td>{corp.city}</td>
                  <td>{corp.state}</td>
                  <td>{corp.zip}</td>
                  <td><StatusBadge status={corp.status} /></td>
                  <td>{corp.startDate}</td>
                  <td>
                    <button
                      onClick={() => handleSwitch(corp)}
                      className={selectedCorp?.id === corp.id ? 'badge badge-success' : 'badge badge-neutral'}
                      style={{ cursor: 'pointer', border: 'none', padding: '4px 12px', borderRadius: '20px', fontWeight: '600', fontSize: '12px' }}
                    >
                      {selectedCorp?.id === corp.id ? 'Active' : 'Switch'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span style={{ fontSize: '13px', color: '#64748b', marginRight: 'auto' }}>
              {typeof totalElements === 'number' ? `${totalElements} total` : ''}
            </span>
            <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:&nbsp;
              <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px' }}>
                {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CorpTable;
